import React from 'react';
import Link from 'next/link';

type ButtonVariant = 'primary' | 'gradient' | 'ghost' | 'outline-gradient';
type ButtonSize = 'unset' | 'sm' | 'md' | 'lg';

interface ButtonOwnProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    className?: string;
    children: React.ReactNode;
}

/**
 * `href` renders the exact same box as an anchor instead of a `<button>`.
 *
 * Without it, a link that has to look like a button ends up as `<Link><Button>`
 * or `<Button><Link>` — nested interactive elements, which screen readers
 * announce twice and which give the two boxes different hit areas.
 */
type ButtonAsButton = ButtonOwnProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps> & {
        href?: undefined;
    };

type ButtonAsLink = ButtonOwnProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonOwnProps> & {
        href: string;
    };

type ButtonProps = ButtonAsButton | ButtonAsLink;

const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    className = '',
    children,
    ...props
}) => {
    // Keep base neutral; sizing is controlled via size prop.
    const baseStyles = "group inline-flex items-center justify-center font-bold cursor-pointer transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none";

    const sizeClasses: Record<ButtonSize, string> = {
        unset: "", // consumer fully controls size via className
        sm: "text-[14px] leading-6 h-10 px-5 rounded-md",
        md: "text-[16px] leading-6.5 h-14.5 px-10 rounded-lg", // original defaults
        lg: "text-[18px] leading-7 h-16 px-12 rounded-xl",
    };

    const variants: Record<ButtonVariant, string> = {
        primary: "bg-[#1B365E] text-white border border-[#EFEFEF] hover:bg-white hover:text-[#1B365E] hover:border-[#1B365E]",
        gradient: "text-white bg-gradient-to-b from-[#FFBC41] to-[#FF5758] hover:shadow-xl hover:brightness-110",
        ghost: "bg-transparent text-[#1B365E] border border-[#1B365E] hover:bg-[#1B365E] hover:text-white",
        "outline-gradient": "[background:linear-gradient(#fff,#fff)padding-box,linear-gradient(180deg,#FFBC41_0%,#FF5758_100%)border-box] border border-transparent hover:bg-linear-to-b hover:from-[#FFBC41] hover:to-[#FF5758] disabled:hover:bg-none",
    };

    const classes = `${baseStyles} ${sizeClasses[size]} ${variants[variant]} ${className}`;

    const content =
        variant === 'outline-gradient' ? (
            /* Disabled: drop the clipped gradient and fall back to a flat grey —
               two competing colour utilities used to make this order-dependent. */
            <span className="flex items-center gap-1.5 md:gap-2.5 bg-linear-to-b from-[#FFBC41] to-[#FF5758] bg-clip-text text-transparent group-hover:text-white transition-colors duration-300 group-disabled:bg-none group-disabled:text-[#6B7280]">
                {children}
            </span>
        ) : (
            children
        );

    if (typeof props.href === 'string') {
        const { href, ...anchorProps } = props as Omit<ButtonAsLink, keyof ButtonOwnProps>;

        // Internal routes go through next/link; absolute URLs and mailto/tel don't.
        const isInternal = href.startsWith('/') || href.startsWith('#');

        return isInternal ? (
            <Link href={href} className={classes} {...anchorProps}>
                {content}
            </Link>
        ) : (
            <a href={href} className={classes} {...anchorProps}>
                {content}
            </a>
        );
    }

    return (
        <button
            className={classes}
            {...(props as Omit<ButtonAsButton, keyof ButtonOwnProps>)}
        >
            {content}
        </button>
    );
};

export default Button;
